import React, {useCallback, useContext, useEffect, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Input from "@amzn/meridian/input";
import Select, {SelectOption} from "@amzn/meridian/select";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import RadioButton from "@amzn/meridian/radio-button";
import Icon from "@amzn/meridian/icon";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import closeTokens from "@amzn/meridian-tokens/base/icon/close";
import Checkbox from "@amzn/meridian/checkbox";
import Tooltip from "@amzn/meridian/tooltip";
import Modal from "@amzn/meridian/modal";
import {DEFAULT_ALERT, CONTAINER_TYPE, CONTAINER_TYPE_DISPLAY_LABELS, CUBE_TYPE, CUBE_TYPE_DISPLAY_LABELS, BELT_STATUS, SETTINGS_TYPE, SORT_PATH_SETTING_STATUS, RESOURCE_TYPE, INDUCT_CONFIG_ALERTS, VALIDATION_ERROR_ALERT_MAP, CSV_HEADERS, CSV_TEMPLATE_HEADER, CSV_TEMPLATE_SAMPLE, CANCEL_CONFIRMATION_MESSAGE, CSV_TEMPLATE_FILENAME} from "./Constants";
import {buildSettingsName} from "./Utils";
import {CycleManagementContext} from "./context/CycleManagementContext";
import Loader from "../loader/Loader";
import {getStationCode} from "../network/NetworkUtil";
import {getSortPathSettings, configureSortPathSettings, getResourcesByNodeId, getStationCycles, refreshConfiguration} from "./network/InductConfigurationResource";
import {FEATURES} from "../util/Constants";
import {getFeaturesFromParent} from "../handler/ParentDataHandler";
const EMPTY_RESOURCES = {nodeResources: []};

const INITIAL_CONFIG = {
    dockDoors: "",
    lanes: {
        hold: "",
        inactive: "",
        pallet: "",
    },
    belts: [],
    priorityLaneCount: null
};

const CONTAINER_TYPES = Object.values(CONTAINER_TYPE);
const CUBE_TYPES = Object.values(CUBE_TYPE);

const toTitleCase = (str) => str.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Matches a dock door name starting with a letter, followed by alphanumeric or hyphens
// e.g. "DD1", "DOCK-A2", "D10"
const DOCK_DOOR_PATTERN = /^[A-Za-z][A-Za-z0-9-]*$/;
// Matches a single lane identifier starting with "IB." followed by a letter and alphanumeric chars
// e.g. "IB.A01", "IB.FRO10", "IB.B5"
const LANE_SINGLE_PATTERN = /^IB\.[A-Za-z][A-Za-z0-9]*$/;
// Matches a lane range with two IB. identifiers separated by ":"
// e.g. "IB.A1:IB.A10", "IB.FRO1:IB.FRO20"
const LANE_RANGE_PATTERN = /^IB\.[A-Za-z][A-Za-z0-9]*:IB\.[A-Za-z][A-Za-z0-9]*$/;
// Strips leading zeros from numeric suffix for comparison
// e.g. "IB.A03" -> "IB.A3", "IB.A10" -> "IB.A10"
const LANE_NORMALIZE_PATTERN = /(\D)0+(\d)/g;
// Matches the "IB." prefix for stripping
// e.g. "IB.A1" -> "A1"
const LANE_PREFIX_PATTERN = /^IB\./;
// Splits identifier into prefix and numeric suffix
// e.g. "A10" -> ["A10", "A", "10"], "FRO5" -> ["FRO5", "FRO", "5"]
const NUMERIC_SUFFIX_PATTERN = /^(.+?)(\d+)$/;

const validateCommaSeparated = (value, ...patterns) => {
    if (!value || value.trim() === "") return true;
    return value.split(",").map(e => e.trim()).every(entry => patterns.some(p => p.test(entry)));
};

const expandLaneRange = (entry) => {
    const parts = entry.split(":");
    if (parts.length !== 2) return [entry];
    const start = parts[0].trim();
    const end = parts[1].trim();
    // Splits identifier into prefix and numeric suffix
    // e.g. "IB.A10" -> ["IB.A10", "IB.A", "10"]
    const startMatch = start.match(NUMERIC_SUFFIX_PATTERN);
    const endMatch = end.match(NUMERIC_SUFFIX_PATTERN);
    if (!startMatch || !endMatch || startMatch[1] !== endMatch[1]) return [entry];
    const prefix = startMatch[1];
    const from = parseInt(startMatch[2], 10);
    const to = parseInt(endMatch[2], 10);
    const padLen = Math.max(startMatch[2].length, endMatch[2].length);
    const result = [];
    for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
        result.push(prefix + String(i).padStart(padLen, '0'));
    }
    return result;
};

const expandLanes = (value) => {
    if (!value) return [];
    return value.split(",").map(e => e.trim()).filter(e => e).flatMap(expandLaneRange);
};

const InductConfigurations = ({intl}) => {
    const {cycleViewModel} = useContext(CycleManagementContext);
    const [edit, setEdit] = useState(false);
    const [selectedCycle, setSelectedCycle] = useState("");
    const [alert, setAlert] = useState(DEFAULT_ALERT);
    const [dockDoors, setDockDoors] = useState(INITIAL_CONFIG.dockDoors);
    const [dockDoorsError, setDockDoorsError] = useState(false);
    const [lanes, setLanes] = useState(INITIAL_CONFIG.lanes);
    const [laneErrors, setLaneErrors] = useState({});
    const [belts, setBelts] = useState(INITIAL_CONFIG.belts);
    const [nodeResources, setNodeResources] = useState(EMPTY_RESOURCES);
    const [csvFile, setCsvFile] = useState(null);
    const [csvFileName, setCsvFileName] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showHoldLaneWarning, setShowHoldLaneWarning] = useState(false);
    const [showSingleBeltWarning, setShowSingleBeltWarning] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [savedConfig, setSavedConfig] = useState(INITIAL_CONFIG);
    const [stationCycles, setStationCycles] = useState([]);
    const [priorityLaneCount, setPriorityLaneCount] = useState(null);
    const [isPriorityLaneCountEnabled, setIsPriorityLaneCountEnabled] = useState(false);
    const [priorityLaneCountError, setPriorityLaneCountError] = useState(false);
    const [refreshingConfiguration, setRefreshingConfiguration] = useState(false);
    const [configPendingUpdate, setConfigPendingUpdate] = useState(false);

    const getAlert = (alertData) => {
        return (alertData.id !== "" ?
            <Alert size="medium" type={alertData.type} onClose={() => setAlert(DEFAULT_ALERT)}>
                <FormattedMessage id={alertData.id} defaultMessage={alertData.default} values={alertData.values}/>
            </Alert> : null)
    }

    const applyConfig = (config) => {
        setDockDoors(config.dockDoors);
        setLanes(config.lanes);
        setBelts(config.belts);
        setPriorityLaneCount(config.priorityLaneCount);
        setSavedConfig(config);
    };

    const fetchConfigForCycle = useCallback(() => {
        if (!selectedCycle) return;
        getResourcesByNodeId(RESOURCE_TYPE.INDUCT_AREA).then(data => {
            setNodeResources(data);
        }).catch(() => {
            setNodeResources(EMPTY_RESOURCES);
        });
        setLoadingConfig(true);
        getSortPathSettings().then(data => {
            const allSettings = data.sortPathSettings || [];
            // Check if any setting has PENDING_UPDATE status to show the Refresh Configuration button.
            // PENDING_UPDATE indicates NARC resources have changed and need to be synced to SPG.
            const hasPendingUpdate = allSettings.some(s => s.status === SORT_PATH_SETTING_STATUS.PENDING_UPDATE);
            setConfigPendingUpdate(hasPendingUpdate);
            const settings = allSettings.filter(s => s.cycle === selectedCycle);
            if (settings.length) {
                const allDoors = settings.flatMap(s => s.activeDockDoors || []);
                const allHold = settings.flatMap(s => (s.inboundStagingLanes || {}).holdLanes || []);
                const allInactive = settings.flatMap(s => (s.inboundStagingLanes || {}).inactiveLanes || []);
                const allContainerTypeLanes = settings.flatMap(s => (s.inboundStagingLanes || {}).containerTypeLanes || []);
                const allPallet = allContainerTypeLanes.filter(ctl => (ctl.containerTypes || []).includes(CONTAINER_TYPE.PALLET)).map(ctl => ctl.label);
                const hasPriorityCount = settings.some(s => (s.inboundStagingLanes || {}).priorityLaneCount !== undefined);
                const priorityCount = hasPriorityCount ? settings.reduce((sum, s) => sum + ((s.inboundStagingLanes || {}).priorityLaneCount || 0), 0) : null;
                const allBelts = settings.flatMap(s => (s.inductBeltConfigurations || []).map(b => ({
                    name: b.belt,
                    status: b.status ? b.status.toUpperCase() : BELT_STATUS.ACTIVE,
                    containerTypes: b.containerTypes || [],
                    cubeTypes: b.cubeTypes || []
                })));
                applyConfig({ dockDoors: allDoors.join(", "), lanes: { hold: allHold.join(", "), inactive: allInactive.join(", "), pallet: allPallet.join(", ") }, belts: allBelts, priorityLaneCount: priorityCount });
            } else {
                return Promise.all([
                    getResourcesByNodeId(RESOURCE_TYPE.INBOUND_LANE),
                    getResourcesByNodeId(RESOURCE_TYPE.DOCK_DOOR)
                ]).then(([laneData, doorData]) => {
                    const holdLanes = [];
                    const inactiveLanes = [];
                    (laneData.nodeResources || []).forEach(r => {
                        const attr = r.resourceAttributes || {};
                        const capacityValue = attr.InboundLaneCapacity;
                        const hasCapacity = capacityValue !== null && capacityValue !== undefined && capacityValue !== "";
                        const capacity = hasCapacity ? Number(capacityValue) : 0;
                        const isHoldEligible = String(attr.isHoldEligible).toLowerCase() === "true";
                        if (capacity > 0 && isHoldEligible) {
                            holdLanes.push(r.label);
                        } else if (!hasCapacity || capacity === 0) {
                            inactiveLanes.push(r.label);
                        }
                    });
                    const doors = (doorData.nodeResources || []).map(r => r.label).join(", ");
                    applyConfig({ dockDoors: doors, lanes: { hold: holdLanes.join(", "), inactive: inactiveLanes.join(", "), pallet: "" }, belts: INITIAL_CONFIG.belts, priorityLaneCount: INITIAL_CONFIG.priorityLaneCount });
                }).catch(() => {
                    applyConfig(INITIAL_CONFIG);
                });
            }
        }).catch(() => {
            applyConfig(INITIAL_CONFIG);
        }).finally(() => {
            setLoadingConfig(false);
        });
        setDockDoorsError(false);
        setLaneErrors({});
        setPriorityLaneCountError(false);
    }, [selectedCycle]);

    const saveConfig = useCallback(() => {
        const stationCode = getStationCode();
        const config = {
            stationCode: stationCode,
            sortPathSetting: {
                name: selectedCycle ? buildSettingsName(selectedCycle, stationCode) : "",
                cycle: selectedCycle || "",
                nodeId: stationCode,
                settingsType: SETTINGS_TYPE,
                activeDockDoors: dockDoors ? dockDoors.split(",").map(d => d.trim()).filter(d => d) : [],
                inductBeltConfigurations: belts.map(belt => ({
                    belt: belt.name || "",
                    status: belt.status ? belt.status.toUpperCase() : BELT_STATUS.ACTIVE,
                    containerTypes: belt.containerTypes || [],
                    cubeTypes: belt.cubeTypes || []
                })),
                inboundStagingLanes: {
                    holdLanes: expandLanes(lanes.hold),
                    inactiveLanes: expandLanes(lanes.inactive),
                    containerTypeLanes: expandLanes(lanes.pallet).map(label => ({label, containerTypes: [CONTAINER_TYPE.PALLET]})),
                    priorityLaneCount: priorityLaneCount
                }
            }
        };
        setSavingConfig(true);
        configureSortPathSettings(config).then((response) => {
            if (response && response.status === 200) {
                setAlert(INDUCT_CONFIG_ALERTS.SAVE_SUCCESS);
                setEdit(false);
                fetchConfigForCycle();
            } else {
                handleApiError(response, INDUCT_CONFIG_ALERTS.SAVE_ERROR);
            }
        }).catch((error) => {
            handleApiError(error, INDUCT_CONFIG_ALERTS.SAVE_ERROR);
        }).finally(() => {
            setSavingConfig(false);
        });
    }, [lanes, dockDoors, belts, selectedCycle, fetchConfigForCycle, priorityLaneCount]);

    const cancelEdit = () => {
        setDockDoors(savedConfig.dockDoors);
        setDockDoorsError(false);
        setLanes(savedConfig.lanes);
        setLaneErrors({});
        setBelts(savedConfig.belts);
        setPriorityLaneCount(savedConfig.priorityLaneCount);
        setPriorityLaneCountError(false);
        setEdit(false);
    }

    const validateDockDoors = (value) => validateCommaSeparated(value, DOCK_DOOR_PATTERN);

    const validateDockDoorsOnBlur = () => {
        setDockDoorsError(!validateDockDoors(dockDoors));
    }

    const validateLaneValue = (value) => {
        if (!value || value.trim() === "") return true;
        return value.split(",").map(e => e.trim()).every(entry => {
            if (LANE_RANGE_PATTERN.test(entry)) {
                // e.g. "IB.A1:IB.A10" -> start="IB.A1", end="IB.A10"
                const [start, end] = entry.split(":");
                // Strips "IB." prefix for prefix comparison
                // e.g. "IB.A1" -> "A1", "IB.FRO10" -> "FRO10"
                const startLabel = start.replace(LANE_PREFIX_PATTERN, "");
                const endLabel = end.replace(LANE_PREFIX_PATTERN, "");
                // Splits into letter prefix and numeric suffix
                // e.g. "A1" -> ["A1", "A", "1"], "FRO10" -> ["FRO10", "FRO", "10"]
                const startMatch = startLabel.match(NUMERIC_SUFFIX_PATTERN);
                const endMatch = endLabel.match(NUMERIC_SUFFIX_PATTERN);
                if (!startMatch || !endMatch || startMatch[1] !== endMatch[1]) return false;
                return true;
            }
            return LANE_SINGLE_PATTERN.test(entry);
        });
    };

    const updateLane = (key, value) => {
        setLanes(prev => ({...prev, [key]: value}));
    }

    const validateLaneOnBlur = (key) => {
        setLaneErrors(prev => ({...prev, [key]: !validateLaneValue(lanes[key])}));
    }

    const hasLaneErrors = () => {
        return Object.values(laneErrors).some(e => e === true);
    }

    const hasErrors = () => {
        return hasLaneErrors() || dockDoorsError || priorityLaneCountError;
    }

    const handleApiError = (error, fallbackAlert) => {
        try {
            const errorResponse = error && error.errorResponse;
            const errorMessage = errorResponse && errorResponse.errorMessage;
            const parsed = errorMessage ? JSON.parse(errorMessage) : null;
            const errorCode = parsed && parsed.message;
            if (errorCode && VALIDATION_ERROR_ALERT_MAP[errorCode]) {
                setAlert({...VALIDATION_ERROR_ALERT_MAP[errorCode], values: {}});
            } else {
                setAlert(fallbackAlert);
            }
        } catch (e) {
            setAlert(fallbackAlert);
        }
    };

    const validateLanes = () => {
        // Strips leading zeros from numeric suffix for comparison
        // e.g. "IB.A03" -> "IB.A3", "IB.A10" -> "IB.A10"
        const normalizeLane = (l) => l.replace(LANE_NORMALIZE_PATTERN, '$1$2');
        const expandedInactive = expandLanes(lanes.inactive).map(normalizeLane);
        const expandedHold = expandLanes(lanes.hold).map(normalizeLane);
        const expandedPallet = expandLanes(lanes.pallet).map(normalizeLane);
        const holdOverlap = expandedInactive.filter(l => expandedHold.includes(l));
        const palletOverlap = expandedInactive.filter(l => expandedPallet.includes(l));
        if (holdOverlap.length > 0) {
            setAlert({id: "inductconfig.inactiveHoldOverlap", default: "Inactive lane(s) " + holdOverlap.join(", ") + " cannot also be in Hold lanes.", values: {lanes: holdOverlap.join(", ")}, type: "error"});
            return false;
        }
        if (palletOverlap.length > 0) {
            setAlert({id: "inductconfig.inactivePalletOverlap", default: "Inactive lane(s) " + palletOverlap.join(", ") + " cannot also be in Pallet lanes.", values: {lanes: palletOverlap.join(", ")}, type: "error"});
            return false;
        }
        return true;
    };

    const validateBelts = () => {
        if (belts.length === 0) {
            setAlert({id: "inductconfig.noBeltsConfigured", default: "No induct belts configured. Please add at least one belt.", values: {}, type: "error"});
            return false;
        }
        const unnamedBelts = belts.filter(b => !b.name || !b.name.trim());
        if (unnamedBelts.length > 0) {
            setAlert(INDUCT_CONFIG_ALERTS.BELT_NAME_REQUIRED);
            return false;
        }
        const activeBeltCount = belts.filter(b => b.status === BELT_STATUS.ACTIVE).length;
        if (activeBeltCount === 1) { setShowSingleBeltWarning(true); return false; }
        return true;
    };

    const handleSaveConfig = () => {
        if (!validateLanes()) return;
        if (!lanes.hold || !lanes.hold.trim()) { setShowHoldLaneWarning(true); return; }
        if (!validateBelts()) return;
        saveConfig();
    };

    const handleRefreshConfiguration = useCallback(() => {
        setRefreshingConfiguration(true);
        refreshConfiguration().then((response) => {
            if (response && response.status === 200) {
                setAlert(INDUCT_CONFIG_ALERTS.REFRESH_SUCCESS);
                setConfigPendingUpdate(false);
            } else {
                setAlert(INDUCT_CONFIG_ALERTS.REFRESH_ERROR);
            }
        }).catch(() => {
            setAlert(INDUCT_CONFIG_ALERTS.REFRESH_ERROR);
        }).finally(() => {
            setRefreshingConfiguration(false);
        });
    }, []);

    const addBelt = () => {
        setBelts(prev => [...prev, {name: "", status: BELT_STATUS.ACTIVE, containerTypes: [CONTAINER_TYPE.PALLET, CONTAINER_TYPE.CART], cubeTypes: [CUBE_TYPE.SMALL, CUBE_TYPE.MEDIUM, CUBE_TYPE.LARGE]}]);
    }

    const removeBelt = (index) => {
        setBelts(prev => prev.filter((_, i) => i !== index));
    }

    const toggleBeltCheckbox = (index, field, value) => {
        setBelts(prev => prev.map((b, i) => {
            if (i !== index) return b;
            const current = b[field];
            const updated = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            return {...b, [field]: updated};
        }));
    }

    const updateBelt = (index, field, value) => {
        setBelts(prev => prev.map((b, i) => i === index ? {...b, [field]: value} : b));
    }

    useEffect(() => {
        getStationCycles().then(data => {
            if (data && data.casCycleDetailsList) {
                setStationCycles(data.casCycleDetailsList.map(c => c.name));
            }
        }).catch(() => {
            setStationCycles([]);
        });
        getFeaturesFromParent([FEATURES.IS_PRIORITY_LANE_COUNT_ENABLED]).then(res => {
            if (res.data && res.data.isPriorityLaneCountEnabled) {
                setIsPriorityLaneCountEnabled(true);
            }
        });
    }, []);

    useEffect(() => {
        fetchConfigForCycle();
    }, [fetchConfigForCycle]);


    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setCsvFile(file);
            setCsvFileName(file.name);
        }
    }

    const uploadCSV = useCallback(() => {
        if (!csvFile) return;
        if (!selectedCycle) {
            setAlert({id: "inductconfig.selectCycleBeforeUpload", default: "Please select a cycle before executing upload.", values: {}, type: "informational"});
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = [];
            let current = "";
            let inQuotes = false;
            for (let i = 0; i < text.length; i++) {
                const ch = text[i];
                if (ch === '"') { inQuotes = !inQuotes; current += ch; }
                else if ((ch === '\n' || ch === '\r') && !inQuotes) {
                    if (ch === '\r' && text[i + 1] === '\n') i++;
                    if (current.trim()) lines.push(current);
                    current = "";
                } else { current += ch; }
            }
            if (current.trim()) lines.push(current);
            if (lines.length < 2) {
                setAlert({...INDUCT_CONFIG_ALERTS.UPLOAD_ERROR, default: "CSV file must contain a header row and at least one data row."});
                return;
            }
            const rows = lines.slice(1).map(line => {
                const cols = [];
                let current = "";
                let inQuotes = false;
                for (let i = 0; i < line.length; i++) {
                    if (line[i] === '"') { inQuotes = !inQuotes; }
                    else if (line[i] === ',' && !inQuotes) { cols.push(current.trim()); current = ""; }
                    else { current += line[i]; }
                }
                cols.push(current.trim());
                return cols;
            });
            const invalidRow = rows.find(row => row[0] && row[0].toUpperCase() !== getStationCode().toUpperCase());
            if (invalidRow) {
                setAlert({...INDUCT_CONFIG_ALERTS.STATION_MISMATCH, default: "Validation failed: Station code in CSV (" + invalidRow[0] + ") does not match the selected station (" + getStationCode() + ")."});
                return;
            }
            const stationCode = rows[0][0] || getStationCode();
            const cycle = rows[0][1] || "";
            if (!cycle || !stationCycles.includes(cycle)) {
                setAlert({id: "inductconfig.invalidCycleInCsv", default: "Invalid or missing cycle in CSV. Please enter a valid cycle.", values: {}, type: "error"});
                return;
            }
            const activeDockDoors = rows[0][2] ? rows[0][2].split(",").map(d => d.trim()).filter(d => d) : [];
            const holdLanes = rows[0][3] ? rows[0][3].split(",").map(l => l.trim()).filter(l => l) : [];
            const inactiveLanes = rows[0][4] ? rows[0][4].split(",").map(l => l.trim()).filter(l => l) : [];
            const palletLanes = rows[0][5] ? rows[0][5].split(",").map(l => l.trim()).filter(l => l) : [];
            const inductBeltConfigurations = [];
            const BELT_COLS = 4;
            const BELT_START = 6;
            for (let i = BELT_START; i < rows[0].length; i += BELT_COLS) {
                const name = rows[0][i] || "";
                if (!name) break;
                inductBeltConfigurations.push({
                    belt: name,
                    status: rows[0][i + 1] ? rows[0][i + 1].toUpperCase() : BELT_STATUS.ACTIVE,
                    containerTypes: rows[0][i + 2] ? rows[0][i + 2].split(",").map(c => c.trim().toUpperCase()).filter(c => c) : [],
                    cubeTypes: rows[0][i + 3] ? rows[0][i + 3].split(",").map(c => c.trim().toUpperCase()).filter(c => c) : []
                });
            }
            const config = {
                stationCode: stationCode,
                sortPathSetting: {
                    name: buildSettingsName(cycle, stationCode),
                    cycle: cycle,
                    nodeId: stationCode,
                    settingsType: SETTINGS_TYPE,
                    activeDockDoors: activeDockDoors,
                    inductBeltConfigurations: inductBeltConfigurations,
                    inboundStagingLanes: {
                        holdLanes: holdLanes,
                        inactiveLanes: inactiveLanes,
                        containerTypeLanes: palletLanes.map(label => ({label, containerTypes: [CONTAINER_TYPE.PALLET]}))
                    }
                }
            };
            setSavingConfig(true);
            configureSortPathSettings(config).then((response) => {
                if (response && response.status === 200) {
                    setAlert(INDUCT_CONFIG_ALERTS.UPLOAD_SUCCESS);
                    fetchConfigForCycle();
                } else {
                    handleApiError(response, INDUCT_CONFIG_ALERTS.UPLOAD_ERROR);
                }
            }).catch((error) => {
                handleApiError(error, INDUCT_CONFIG_ALERTS.UPLOAD_ERROR);
            }).finally(() => {
                setSavingConfig(false);
            });
        };
        reader.readAsText(csvFile);
    }, [csvFile, selectedCycle, fetchConfigForCycle]);

    const downloadCSV = useCallback(() => {
        const stationCode = getStationCode();
        const header = CSV_HEADERS +
            belts.map((_, i) => `,belt_${i+1}_name,belt_${i+1}_status,belt_${i+1}_container_types,belt_${i+1}_cube_types`).join("");
        const beltData = belts.map(belt => `,"${belt.name}","${belt.status}","${(belt.containerTypes || []).join(", ")}","${(belt.cubeTypes || []).join(", ")}"`).join("");
        const row = `"${stationCode}","${selectedCycle}","${dockDoors}","${lanes.hold}","${lanes.inactive}","${lanes.pallet}"${beltData}`;
        const csvContent = [header, row].join("\n");
        const blob = new Blob([csvContent], {type: "text/csv"});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `induct-configuration-${stationCode}-${selectedCycle || "all"}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }, [belts, dockDoors, lanes, selectedCycle]);

    const downloadTemplate = () => {
        const csvContent = [CSV_TEMPLATE_HEADER, CSV_TEMPLATE_SAMPLE].join("\n");
        const blob = new Blob([csvContent], {type: "text/csv"});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = CSV_TEMPLATE_FILENAME;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    const getUploadSection = () => {
        return (
            <Box type="outline" spacingInset="400">
                <Column spacing="300">
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Text><b>
                            <FormattedMessage id="inductconfig.uploadCsv" defaultMessage="Upload Configuration CSV"/>
                        </b></Text>
                        {edit && selectedCycle !== "" && <Row spacing="200">
                            <Button type="secondary" onClick={() => setShowCancelModal(true)}>
                                <FormattedMessage id="inductconfig.cancel" defaultMessage="Cancel"/>
                            </Button>
                            <Button type="primary" onClick={handleSaveConfig} disabled={hasErrors()}>
                                <FormattedMessage id="inductconfig.save" defaultMessage="Save Configuration"/>
                            </Button>
                        </Row>}
                    </Row>
                    <Row spacing="300" alignmentVertical="center">
                        <label htmlFor="csvFileInput" style={{
                            background: "#FFFFFF", border: "1px solid #D1D1D1", padding: "8px 16px",
                            borderRadius: "4px", fontWeight: 700, cursor: "pointer", fontSize: "14px"
                        }}>
                            <FormattedMessage id="inductconfig.chooseFile" defaultMessage="Choose File"/>
                        </label>
                        <input id="csvFileInput" type="file" accept=".csv" onChange={handleFileSelect}
                               onClick={(e) => { e.target.value = null; }}
                               style={{display: "none"}}/>
                        <Text color="secondary">
                            {csvFileName || intl.formatMessage({id: "inductconfig.noFile", defaultMessage: "No file chosen"})}
                        </Text>
                        {csvFile && <Button type="icon" onClick={() => { setCsvFile(null); setCsvFileName(""); }}>
                            <Icon tokens={closeTokens}>Clear file</Icon>
                        </Button>}
                        {csvFile && <Button type="primary" onClick={uploadCSV}>
                            <FormattedMessage id="inductconfig.executeUpload" defaultMessage="Execute Upload"/>
                        </Button>}
                        <Row spacing="200" style={{marginLeft: "auto"}}>
                            <Tooltip position="top" title="Download current configuration as CSV">
                                <Button type="secondary" onClick={downloadCSV}>
                                    <FormattedMessage id="inductconfig.downloadCsv" defaultMessage="Download CSV"/>
                                </Button>
                            </Tooltip>
                            <Tooltip position="top" title="Download sample template with column headers">
                                <Button type="secondary" onClick={downloadTemplate}>
                                    <FormattedMessage id="inductconfig.downloadTemplate" defaultMessage="Download Template"/>
                                </Button>
                            </Tooltip>
                        </Row>
                    </Row>
                </Column>
            </Box>
        )
    }

    const getTableActionBar = () => {
        const cycleNames = stationCycles;
        return (
            <Box type="outline" spacingInset="400">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Row spacing="200">
                        <Select
                            value={selectedCycle}
                            onChange={setSelectedCycle}
                            placeholder={intl.formatMessage({id: "inductconfig.selectCycle", defaultMessage: "Select a cycle..."})}
                            width={200}>
                            {cycleNames.map(c => <SelectOption key={c} value={c} label={c}/>)}
                            {!cycleNames.length ? (
                                <Column alignmentVertical="center" spacing="100" spacingInset="100">
                                    <Text alignment="center">
                                        <FormattedMessage id="inductconfig.noCycles" defaultMessage="No cycles available"/>
                                    </Text>
                                </Column>
                            ) : null}
                        </Select>
                    </Row>
                    <Row spacing="200">
                        {configPendingUpdate && <Button type="secondary" onClick={handleRefreshConfiguration} disabled={refreshingConfiguration}>
                            {refreshingConfiguration
                                ? <FormattedMessage id="inductconfig.refreshing" defaultMessage="Refreshing..."/>
                                : <FormattedMessage id="inductconfig.refreshConfiguration" defaultMessage="Refresh Configuration"/>
                            }
                        </Button>}
                        {selectedCycle !== "" && !edit && !loadingConfig && <Button type="primary" onClick={() => setEdit(true)}>
                            <FormattedMessage id="inductconfig.clickToEdit" defaultMessage="Click to Edit"/>
                        </Button>}
                        {selectedCycle !== "" && edit && <Button type="primary" onClick={() => setEdit(false)}>
                            <FormattedMessage id="inductconfig.editingMode" defaultMessage="Editing Mode"/>
                        </Button>}
                    </Row>
                </Row>
            </Box>
        )
    }

    const getDockDoorsTable = () => {
        return (
            <Box type="outline" spacingInset="400">
                <Row spacing="300" alignmentVertical="top" widths={["200px", "fill"]}>
                    <Column spacing="none">
                        <Text><b>Active Dock Doors</b></Text>
                        <Text color="secondary">(comma-separated)</Text>
                    </Column>
                    <div style={{minHeight: 52}}>
                        <Input value={dockDoors} onChange={setDockDoors}
                               onBlur={validateDockDoorsOnBlur}
                               disabled={!edit}
                               error={dockDoorsError}/>
                        <Text color={dockDoorsError ? "error" : "secondary"}>
                            <FormattedMessage id="inductconfig.dockDoorsFormatError"
                                              defaultMessage="Use door names (e.g. DD1), separated by commas"/>
                        </Text>
                    </div>
                </Row>
            </Box>
        )
    }

    const getLanesTable = () => {
        const laneFields = [
            {key: "hold", id: "inductconfig.holdLanes", defaultMessage: "Hold Lanes"},
            {key: "inactive", id: "inductconfig.inactiveLanes", defaultMessage: "Inactive Lanes"},
            {key: "pallet", id: "inductconfig.palletLanes", defaultMessage: "Pallet Lanes"},
        ];

        return (
            <Box type="outline" spacingInset="400">
                <Column spacing="300">
                    <Row spacing="300" alignmentVertical="top" widths={["200px", "fill"]}>
                        <Column spacing="none">
                            <Text><b><FormattedMessage id="inductconfig.priorityLaneCount" defaultMessage="Priority Lane Count"/></b></Text>
                        </Column>
                        <div style={{minHeight: 52}}>
                            <Input value={priorityLaneCount !== null ? String(priorityLaneCount) : ""}
                                   onChange={v => {
                                       if (v === "") { setPriorityLaneCount(null); setPriorityLaneCountError(false); return; }
                                       const num = Number(v);
                                       if (Number.isInteger(num) && num >= 0) { setPriorityLaneCount(num); setPriorityLaneCountError(false); }
                                   }}
                                   disabled={!edit || !isPriorityLaneCountEnabled}
                                   error={priorityLaneCountError}
                                   type="number"/>
                            <Text color={priorityLaneCountError ? "error" : "secondary"}>
                                <FormattedMessage id="inductconfig.priorityLaneCountHint" defaultMessage="Use priority lanes count (e.g. 25)"/>
                            </Text>
                        </div>
                    </Row>
                    {laneFields.map(({key, id, defaultMessage}) =>
                        <Row key={key} spacing="300" alignmentVertical="top" widths={["200px", "fill"]}>
                            <Column spacing="none">
                                <Text><b><FormattedMessage id={id} defaultMessage={defaultMessage}/></b></Text>
                                <Text color="secondary">(comma-separated)</Text>
                            </Column>
                            <div style={{minHeight: 52}}>
                                <Input value={lanes[key]}
                                       onChange={v => updateLane(key, v)}
                                       onBlur={() => validateLaneOnBlur(key)}
                                       disabled={!edit}
                                       error={laneErrors[key]}/>
                                <Text color={laneErrors[key] ? "error" : "secondary"}>
                                    <FormattedMessage id="inductconfig.laneFormatError"
                                                      defaultMessage="Use single values (e.g. IB.A23) or ranges (e.g. IB.A1:IB.A10), separated by commas"/>
                                </Text>
                            </div>
                        </Row>
                    )}

                </Column>
            </Box>
        )
    }

    const getBeltsSection = () => {
        return (
            <Column spacing="300">
                <Text><b>
                    <FormattedMessage id="inductconfig.belts" defaultMessage="Induct Belts"/>
                </b></Text>
                {belts.map((belt, index) => ({belt, originalIndex: index}))
                    .sort((a, b) => (a.belt.name || "").localeCompare(b.belt.name || ""))
                    .map(({belt, originalIndex}) =>
                    <Box key={originalIndex} type="outline" spacingInset="400">
                        <div style={{position: "relative"}}>
                            {edit && <div style={{position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)"}}>
                                <Tooltip position="top" title="Warning: This item would be removed"><Button type="secondary" onClick={() => removeBelt(originalIndex)}>
                                    <FormattedMessage id="inductconfig.discard" defaultMessage="Discard"/>
                                </Button></Tooltip>
                            </div>}
                            <Column spacing="300">
                                <Row spacing="400" widths={["fill", "fill"]} alignmentVertical="top">
                                    <Row spacing="300" alignmentVertical="center" widths={["auto", "fill"]}>
                                        <Text><b>Belt :</b></Text>
                                        <Select value={belt.name}
                                                onChange={v => updateBelt(originalIndex, "name", v)}
                                                disabled={!edit}
                                                width="100%"
                                                placeholder={intl.formatMessage({id: "inductconfig.selectBelt", defaultMessage: "Select a belt..."})}>
                                            {nodeResources.nodeResources
                                                .filter(r => r.label === belt.name || !belts.some(b => b.name === r.label))
                                                .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
                                                .map(r => <SelectOption key={r.label} value={r.label} label={r.label}/>)}
                                        </Select>
                                    </Row>
                                    <Row spacing="300" alignmentVertical="center">
                                        <Text><b>Status :</b></Text>
                                        <RadioButton
                                            checked={belt.status === BELT_STATUS.ACTIVE}
                                            onChange={() => updateBelt(originalIndex, "status", BELT_STATUS.ACTIVE)}
                                            disabled={!edit}>
                                            <FormattedMessage id="inductconfig.active" defaultMessage="Active"/>
                                        </RadioButton>
                                        <RadioButton
                                            checked={belt.status === BELT_STATUS.INACTIVE}
                                            onChange={() => updateBelt(originalIndex, "status", BELT_STATUS.INACTIVE)}
                                            disabled={!edit}>
                                            <FormattedMessage id="inductconfig.inactive" defaultMessage="Inactive"/>
                                        </RadioButton>
                                    </Row>
                                </Row>
                                <Row spacing="400" widths={["fill", "fill"]} alignmentVertical="top">
                                    <Column spacing="200">
                                        <Text><b>Container Types :</b></Text>
                                        {CONTAINER_TYPES.map(ct =>
                                            <Checkbox
                                                key={ct}
                                                checked={belt.containerTypes.includes(ct)}
                                                onChange={() => toggleBeltCheckbox(originalIndex, "containerTypes", ct)}
                                                disabled={!edit}>
                                                {CONTAINER_TYPE_DISPLAY_LABELS[ct] || toTitleCase(ct)}
                                            </Checkbox>
                                        )}
                                    </Column>
                                    <Column spacing="200">
                                        <Text><b>Cube Types :</b></Text>
                                        {CUBE_TYPES.map(cube =>
                                            <Checkbox
                                                key={cube}
                                                checked={belt.cubeTypes.includes(cube)}
                                                onChange={() => toggleBeltCheckbox(originalIndex, "cubeTypes", cube)}
                                                disabled={!edit}>
                                                {CUBE_TYPE_DISPLAY_LABELS[cube] || toTitleCase(cube)}
                                            </Checkbox>
                                        )}
                                    </Column>
                                </Row>
                            </Column>
                        </div>
                    </Box>
                )}
                {edit && <div style={{paddingBottom: "16px"}}><Row alignmentHorizontal="center" alignmentVertical="center" spacing="200">
                    <Button type="icon" onClick={addBelt}>
                        <Icon tokens={plusCircleTokens}>
                            <FormattedMessage id="inductconfig.addBelt" defaultMessage="Add belt"/>
                        </Icon>
                    </Button>
                    <Text><b>Add a new belt configuration</b></Text>
                </Row></div>}
            </Column>
        )
    }

    if (cycleViewModel.loadingCycle) {
        return (<Loader/>)
    }

    return (
        <Column spacing="300">
            {getAlert(alert)}
            {savingConfig && <Loader/>}
            {getUploadSection()}
            {getTableActionBar()}
            {loadingConfig && <Loader/>}
            {!loadingConfig && selectedCycle !== "" && getDockDoorsTable()}
            {!loadingConfig && selectedCycle !== "" && getLanesTable()}
            {!loadingConfig && selectedCycle !== "" && getBeltsSection()}
            <Modal
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                closeLabel="Close"
            >
                <Text>{CANCEL_CONFIRMATION_MESSAGE}</Text>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" onClick={() => setShowCancelModal(false)}>No</Button>
                    <Button type="primary" onClick={() => { setShowCancelModal(false); cancelEdit(); }}>Yes</Button>
                </Row>
            </Modal>
            <Modal
                open={showHoldLaneWarning}
                onClose={() => setShowHoldLaneWarning(false)}
                closeLabel="Close"
            >
                <Text><FormattedMessage id="inductconfig.holdLaneWarning" defaultMessage="No hold lanes configured. Containers cannot be held during this cycle. Do you want to proceed?"/></Text>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" onClick={() => setShowHoldLaneWarning(false)}>No</Button>
                    <Button type="primary" onClick={() => {
                        setShowHoldLaneWarning(false);
                        if (!validateLanes()) return;
                        if (!validateBelts()) return;
                        saveConfig();
                    }}>Yes</Button>
                </Row>
            </Modal>
            <Modal
                open={showSingleBeltWarning}
                onClose={() => setShowSingleBeltWarning(false)}
                closeLabel="Close"
            >
                <Text><FormattedMessage id="inductconfig.singleBeltWarning" defaultMessage="Only one active belt configured. If this belt goes down, no packages can be inducted. Do you want to proceed?"/></Text>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" onClick={() => setShowSingleBeltWarning(false)}>No</Button>
                    <Button type="primary" onClick={() => { setShowSingleBeltWarning(false); saveConfig(); }}>Yes</Button>
                </Row>
            </Modal>
        </Column>
    )
}

export default injectIntl(InductConfigurations);



// WEBPACK FOOTER //
// ./src/components/inductmanagementv2/InductConfigurations.js